import React from 'react';
import { cn } from '../utils/cn';

export interface EmptyStateProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title'> {
  /** Optional illustration / icon. If not provided, a tasteful default appears. */
  illustration?: React.ReactNode;
  title: React.ReactNode;
  description?: React.ReactNode;
  action?: React.ReactNode;
  /** When true, render as a softer inline empty state (e.g. inside a section). */
  compact?: boolean;
  /** Use a tonal background instead of plain surface. */
  tone?: 'surface' | 'tonal' | 'transparent';
}

const DefaultIllustration: React.FC = () => (
  <svg
    viewBox="0 0 120 120"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className="h-24 w-24 text-primary/30"
    aria-hidden
  >
    <circle cx="60" cy="60" r="48" fill="currentColor" opacity="0.12" />
    <circle cx="60" cy="60" r="32" stroke="currentColor" strokeWidth="2" strokeDasharray="4 4" />
    <path
      d="M40 60 L55 75 L82 48"
      stroke="currentColor"
      strokeWidth="3.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const EmptyState: React.FC<EmptyStateProps> = ({
  illustration,
  title,
  description,
  action,
  compact = false,
  tone = 'tonal',
  className,
  ...props
}) => (
  <div
    className={cn(
      'flex flex-col items-center justify-center text-center',
      compact ? 'gap-3 px-4 py-6' : 'gap-4 px-6 py-12',
      tone === 'surface' && 'rounded-md bg-surface border border-outline/30',
      tone === 'tonal' && 'rounded-md bg-surface-variant/60',
      tone === 'transparent' && 'bg-transparent',
      className,
    )}
    {...props}
  >
    <div className={cn('flex items-center justify-center', compact && '[&>svg]:h-14 [&>svg]:w-14')}>
      {illustration ?? <DefaultIllustration />}
    </div>
    <h3 className={cn('font-semibold text-surface-foreground', compact ? 'text-title-md' : 'text-title-lg')}>
      {title}
    </h3>
    {description ? (
      <p className="max-w-prose text-pretty text-body-md text-muted-foreground">{description}</p>
    ) : null}
    {action ? <div className="mt-1 flex flex-wrap items-center justify-center gap-2">{action}</div> : null}
  </div>
);
